import styled from "@emotion/styled";
import { FormProvider, useForm } from "react-hook-form";
import { baseUrl, postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { RichtextField } from "@kaltura/mediaspace-shared-ui/controlled-form";
import { NameField } from "@kaltura/mediaspace-shared-ui";
import { useEffect, KeyboardEventHandler } from "react";
import { createdPlaylistStorageKey } from "@kaltura/mediaspace-shared-constants";

const StyledForm = styled("form")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
}));

type CreatePlaylistResponse = {
    success: boolean;
    redirectUrl?: string;
    errorMessage?: string;
    createdPlaylistId: string;
    createdPlaylistName: string;
};

export interface CreatePlaylistFormProps {
    /** Form ID to be used for form submission */
    formId: string;
    /** Function to update parent's processing state */
    updateProcessing: (value: boolean) => void;
    /** Function to update parent's disable button state */
    updateDisableButton: (value: boolean) => void;
    /** submit URL for creating the playlist */
    submitUrl?: string;
    /** Indicates whether the playlist is a channel playlist */
    channelId?: number | string;
    /** callback function to be called after playlist creation */
    onCreate?: (createdPlaylistId: string, createdPlaylistName: string) => void;
}

const baseClassName = "kms-ds-create-playlist-form";

/**
 * Form for creating a new personal/channel playlist
 */
const CreatePlaylistForm = (props: CreatePlaylistFormProps) => {
    const {
        formId,
        updateProcessing,
        updateDisableButton,
        submitUrl = `${baseUrl}/default/playlist2/create-playlist`,
        channelId,
        onCreate,
    } = props;
    const sendButtonAnalytics = useButtonAnalytics();
    const { showToast } = useToastsContext();

    const useFormHookMethods = useForm({
        mode: "onChange",
        defaultValues: {
            name: "",
            description: "",
        },
    });

    // Prevent key events from bubbling to parent menus (which may have typeahead and steal focus)
    const stopKeyPropagation: KeyboardEventHandler<HTMLFormElement> = (e) => {
        e.stopPropagation();
    };

    // Update parent component when form validation state changes
    useEffect(() => {
        updateDisableButton(!useFormHookMethods.formState.isValid);
    }, [useFormHookMethods.formState.isValid, updateDisableButton]);

    const onSubmit = async (data: { [key: string]: string | boolean | number }) => {
        updateProcessing(true);

        if (channelId) {
            data.channelId = channelId;
        }

        try {
            const result = (await postKmsData(submitUrl, data)) as CreatePlaylistResponse;
            if (result.success) {
                // redirect to the new playlist edit page on success
                sendButtonAnalytics(
                    "Create playlist - DS",
                    ButtonClickAnalyticsType.CREATE,
                    undefined,
                    "Addtoplaylists",
                    `${channelId ? "channel" : "personal"} playlist creation modalbox`
                );
                if (result.redirectUrl) {
                    try {
                        // Persist the created playlist id so the destination page can open the edit view after redirect.
                        window.sessionStorage.setItem(createdPlaylistStorageKey, result.createdPlaylistId);
                    }
                    catch {
                        // ignore storage failures and just redirect
                    }
                    window.location.href = result.redirectUrl;
                }
                else {
                    onCreate?.(result.createdPlaylistId, result.createdPlaylistName);
                }
                updateProcessing(false);
            }
            if (!result.success) {
                // show toast in case of other errors
                showToast({ severity: "error", message: result.errorMessage });
                updateProcessing(false);
            }
        }
        catch (e) {
            updateProcessing(false);
            showToast({ severity: "error", message: translate("Failed to create playlist") });
        }
    };

    return (
        <FormProvider {...useFormHookMethods}>
            <StyledForm
                id={formId}
                className={baseClassName}
                onSubmit={useFormHookMethods.handleSubmit(onSubmit)}
                onKeyDown={stopKeyPropagation}
            >
                <NameField id={"name"} label={translate("Name")} className={`${baseClassName}-name`} />
                <RichtextField
                    label={translate("Description")}
                    id={"description"}
                    fontWeightBold={true}
                    inline={true}
                    minInputHeight={140}
                />
            </StyledForm>
        </FormProvider>
    );
};
export default CreatePlaylistForm;
